import { createTheme, ThemeOptions } from '@mui/material/styles';

/**
 * Blakeney Racquet & Swim Club Theme
 * 
 * This theme is designed to match the Blakeney Club brand identity
 * based on their website at https://blakeneyclub.com/
 * 
 * Brand Characteristics:
 * - Premium, exclusive feel
 * - Clean, modern design
 * - Professional tennis club aesthetic
 * - Elegant typography
 * - Sophisticated color palette
 */

// Color palette inspired by Blakeney Club's website
const colors = {
  // Primary colors - Deep, sophisticated blues and greens
  primary: {
    main: '#1a4d5c',      // Deep teal/blue-green (premium feel)
    light: '#2d6b7f',     // Lighter teal for hover states
    dark: '#0f2e38',      // Darker teal for emphasis
    contrastText: '#ffffff',
  },
  // Secondary colors - Warm, inviting accents
  secondary: {
    main: '#c9a961',      // Gold/amber accent (luxury feel)
    light: '#d4b87a',     // Lighter gold
    dark: '#a68a4f',      // Darker gold
    contrastText: '#ffffff',
  },
  // Neutral colors
  background: {
    default: '#f8f9fa',   // Very light gray/off-white
    paper: '#ffffff',     // Pure white for cards
  },
  text: {
    primary: '#2c3e50',   // Dark gray-blue for main text
    secondary: '#5a6c7d', // Medium gray for secondary text
  },
  // Status colors
  success: {
    main: '#28a745',      // Green for success states
    light: '#6cbf47',
    dark: '#1e7e34',
  },
  error: {
    main: '#dc3545',      // Red for errors
    light: '#e4606d',
    dark: '#c82333',
  },
  warning: {
    main: '#ffc107',      // Amber for warnings
    light: '#ffcd39',
    dark: '#e0a800',
  },
  info: {
    main: '#17a2b8',      // Teal for info
    light: '#3fc1d8',
    dark: '#138496',
  },
};

// Typography settings
const typography: ThemeOptions['typography'] = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  h1: {
    fontSize: '2.5rem',
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    color: colors.text.primary,
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    color: colors.text.primary,
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.4,
    color: colors.text.primary,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    color: colors.text.primary,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
    color: colors.text.primary,
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.5,
    color: colors.text.primary,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
    color: colors.text.primary,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: colors.text.secondary,
  },
  button: {
    fontWeight: 500,
    textTransform: 'none', // Keep buttons in sentence case
    letterSpacing: '0.02em',
  },
};

// Component customizations
const components: ThemeOptions['components'] = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '10px 24px',
        fontSize: '0.9375rem',
        fontWeight: 500,
        textTransform: 'none',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        },
      },
      contained: {
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          transition: 'box-shadow 0.3s ease-in-out',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        fontWeight: 500,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
          },
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
        backgroundColor: '#ffffff',
        color: colors.text.primary,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: '#ffffff',
        borderRight: '1px solid rgba(0, 0, 0, 0.08)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
      elevation1: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      },
      elevation2: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      },
      elevation3: {
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.9375rem',
      },
    },
  },
};

// Create and export the theme
export const blakeneyTheme = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    background: colors.background,
    text: colors.text,
    success: colors.success,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
  },
  typography,
  components,
  shape: {
    borderRadius: 8,
  },
  spacing: 8, // 8px base spacing unit
});

export default blakeneyTheme;

